import Close from "@assets/close.svg";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { useState } from "react";
import { colours } from "../../design/colours";
import { IconButton } from "./buttons";
import { FlexRowContainer } from "./layout";

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colours.uIGrey.light._5};
  border-radius: 12px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  height: min-content;
  max-height: 90vh;
  max-height: 90dvh;
  width: ${({ theme }) => theme.spacing(93.75)};
  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    width: 90%;
  }
  padding: 0;
`;

const Overlay = styled(FloatingOverlay)`
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  z-index: 10;
`;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly ref?: Ref<HTMLButtonElement>;
}
interface ModalProps {
  readonly renderButton: (props: ButtonProps) => ReactNode;
  readonly header: ReactNode;
  readonly subheader?: ReactNode;
  readonly content: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  readonly isOpen?: boolean;
  readonly onClose?: () => void;
}

export const Modal = ({
  renderButton,
  className,
  header,
  content,
  footer,
  isOpen: controlledIsOpen,
  onClose,
}: ModalProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? internalIsOpen;

  const { context, refs } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setInternalIsOpen(open);
      if (!open && onClose) onClose();
    },
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePress: false });
  const role = useRole(context, { role: "dialog" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const headerId = useId();

  return (
    <div role="none">
      {renderButton({
        ref: refs.setReference,
        ...getReferenceProps({
          className,
          onClick: () => setInternalIsOpen(true),
        }),
      })}
      {isOpen && (
        <FloatingPortal id="root">
          <Overlay lockScroll>
            <FloatingFocusManager context={context}>
              <Dialog
                ref={refs.setFloating}
                aria-labelledby={headerId}
                {...getFloatingProps()}
              >
                <FlexRowContainer
                  id={headerId}
                  className="modal-header"
                  style={{
                    alignItems: "flex-start",
                    backgroundColor: colours.uIGrey.light._10,
                    borderRadius: "12px 12px 0 0",
                    padding: "1rem",
                  }}
                >
                  {header}
                  <IconButton
                    onClick={() => {
                      setInternalIsOpen(false);
                      if (onClose) onClose();
                    }}
                    src={Close}
                    alt="Close"
                    style={{
                      height: "24px",
                      width: "24px",
                    }}
                  />
                </FlexRowContainer>
                <div style={{ overflow: "auto", padding: "1rem" }}>
                  {content}
                </div>
                {footer && (
                  <FlexRowContainer
                    css={({ colours }) => css`
                      background-color: ${colours.uIGrey.light._10};
                      border-radius: 0 0 12px 12px;
                      border-top: 2px solid ${colours.accent.light._20};
                      justify-content: flex-end;
                      padding: 1rem;
                    `}
                  >
                    {footer}
                  </FlexRowContainer>
                )}
              </Dialog>
            </FloatingFocusManager>
          </Overlay>
        </FloatingPortal>
      )}
    </div>
  );
};
